import { useEffect, useState, useMemo, useCallback } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient, InfiniteData } from '@tanstack/react-query';
import { APP_CONFIG } from '@/constants/constants';
import { ProductRead, SortDirection, FormStatus, StockStatus, QueryAttributeMatch } from '@/types';
import { logger } from '@/utils/secureLogger';
import { 
    getProductDoc, 
    getProductDocs, 
    addProductDoc, 
    setProductDoc, 
    deleteProductDoc,
    restoreProductDoc
} from '@/services/firebaseApi';
import useCategories from './useCategories';
import useLocations from './useLocations';
import { trackCreate, trackDelete, trackEdit, trackRestore } from '@/services/analytics';

export enum ProductsSortBy {
    SKU = 'sku',
    NAME = 'name',
    AMOUNT = 'amount',
    LOCATION = 'location',
}

export enum ProductDraftFilter {
    ALL = 'all',
    DRAFTS_ONLY = 'draftsOnly',
    PUBLISHED_ONLY = 'publishedOnly',
}

interface UseProductsProps {
    storageId?: string;
    productId?: string;
    categoryId?: string;
    locationId?: string;
}

const useProducts = ({ 
    storageId, 
    productId, 
    categoryId,
    locationId
}: UseProductsProps) => {
    const queryClient = useQueryClient();
    
    // Filter states (keeping the same local state as before)
    const [sortBy, setSortBy] = useState(ProductsSortBy.NAME);
    const [sortDirection, setSortDirection] = useState(SortDirection.ASCENDING);
    const [filterQuery, setFilterQuery] = useState<string>('');
    const [filterCategory, setFilterCategory] = useState<string>('');
    const [filterLocation, setFilterLocation] = useState<string>('');
    const [filterStockStatus, setFilterStockStatus] = useState<StockStatus>(StockStatus.ALL);
    const [filterDraftStatus, setFilterDraftStatus] = useState<ProductDraftFilter>(ProductDraftFilter.ALL);
    const [displayDeleted, setDisplayDeleted] = useState<boolean>(false);
    const [queryMatchingAttributes, setQueryMatchingAttributes] = useState<QueryAttributeMatch>({});

    // Get category related functions
    const {
        getCategoryNamesString
    } = useCategories({ storageId });

    const {
        getLocationNamesString
    } = useLocations({ storageId });

    // Set category filter when categoryId changes
    useEffect(() => {
        setFilterCategory(categoryId || '');
    }, [categoryId]);

    useEffect(() => {
        setFilterLocation(locationId || '');
    }, [locationId]);

    // Single product query
    const {
        data: product,
        isLoading: productLoading,
        error: productError,
        refetch: refetchProduct
    } = useQuery({
        queryKey: ['product', storageId, productId],
        queryFn: () => getProductDoc({ storageId: storageId!, productId: productId! }),
        enabled: !!(storageId && productId),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });

    // Products list query with infinite pagination
    const {
        data: productsData,
        isLoading: productsLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        error: productsError,
        refetch: refetchProducts
    } = useInfiniteQuery<ProductRead[], Error, InfiniteData<ProductRead[]>, (string | undefined | boolean | ProductsSortBy | SortDirection)[], string | number | undefined>({
        queryKey: ['products', storageId, displayDeleted, sortBy, sortDirection],
        queryFn: ({ pageParam }) => getProductDocs({
            storageId: storageId!,
            docLimit: APP_CONFIG.DEFAULT_DOC_LIMIT,
            startAfterData: pageParam as string | number | null | undefined,
            sortBy,
            sortDirection,
            includeDeleted: displayDeleted
        }),
        enabled: !!storageId && !productId,
        getNextPageParam: (lastPage: ProductRead[]): string | number | undefined => {
            if (lastPage.length < APP_CONFIG.DEFAULT_DOC_LIMIT) return undefined;
            const lastItem = lastPage[lastPage.length - 1];
            
            // Handle nested field access for 'amount'
            let value: string | number | undefined;
            if (sortBy === ProductsSortBy.AMOUNT) {
                value = lastItem.status?.currentAmount;
            } else {
                value = lastItem[sortBy as keyof ProductRead] as string | number | undefined;
            }
            
            // Handle undefined/null values (especially for location)
            // Return empty string for undefined to maintain pagination continuity
            if (value === undefined || value === null) {
                return '';
            }
            
            return typeof value === 'string' || typeof value === 'number' ? value : undefined;
        },
        staleTime: 3 * 60 * 1000, // 3 minutes for lists
        gcTime: 10 * 60 * 1000,
        initialPageParam: undefined,
    });

    // Flatten infinite query results
    const products = useMemo(() => {
        return productsData?.pages.flat() || []
    }, [productsData])

    // Helper function for data matching (similar to useItems)
    const dataIsMatch = useCallback((customInputData: string | boolean | number, value: string) => {
        switch (typeof customInputData) {
            case "string":
                if (value === ``) {
                    return customInputData === ``;
                } else {
                    return customInputData.toString().toLowerCase().includes(value);
                }
            case "number":
                return customInputData === Number(value);
            case "boolean":
                return customInputData === (value === "true");
            default:
                return false;
        }
    }, []);

    // Apply client-side filtering with advanced query support
    const filteredProducts = useMemo(() => {
        let filteredList = [...products];
        let newQueryMatchingAttributes = {} as QueryAttributeMatch;
        
        const addToQueryMatchingAttributes = (categoryId: string, customInputName: string) => {
            if (
                newQueryMatchingAttributes[categoryId] &&
                newQueryMatchingAttributes[categoryId].includes(customInputName)
            ) return;
            newQueryMatchingAttributes = {
                ...newQueryMatchingAttributes,
                [categoryId]: [
                    ...(newQueryMatchingAttributes[categoryId] || []),
                    customInputName
                ]
            };
        };

        // Apply text/query filtering
        if (filterQuery) {
            const lowerCaseQuery = filterQuery.toLowerCase().trim();
            
            // Check if it's a structured query (contains colon)
            if (lowerCaseQuery.includes(":")) {
                // Split query into parts
                const queryParts = filterQuery.split(",");
                
                // Loop each query part
                queryParts.forEach(part => {
                    // Split each query part into attribute name and value
                    let [attributeName, attributeValue] = part.split(":");
                    if (!attributeName || !attributeValue) return;
                    attributeName = attributeName.trim().toLowerCase();
                    attributeValue = attributeValue.trim().toLowerCase();
                    
                    // Filter based on attribute
                    filteredList = filteredList.filter((product: ProductRead) => {
                        let matches = false;
                        const categoryString = getCategoryNamesString(product.categories);
                        const locationString = getLocationNamesString(product.locations || []);

                        switch (attributeName) {
                            case "code":
                            case "sku":
                                matches = dataIsMatch(product.sku, attributeValue);
                                if (matches) addToQueryMatchingAttributes("_", "sku");
                                break;
                            case "name":
                                matches = dataIsMatch(product.name, attributeValue);
                                if (matches) addToQueryMatchingAttributes("_", "name");
                                break;
                            case "description":
                                matches = dataIsMatch(product.description || "", attributeValue);
                                if (matches) addToQueryMatchingAttributes("_", "description");
                                break;
                            case "location":
                                matches = dataIsMatch(locationString, attributeValue) || dataIsMatch(product.location || "", attributeValue);
                                if (matches) addToQueryMatchingAttributes("_", "location");
                                break;
                            case "unit":
                                matches = dataIsMatch(product.unit || "", attributeValue);
                                if (matches) addToQueryMatchingAttributes("_", "unit");
                                break;
                            case "category":
                                matches = dataIsMatch(categoryString, attributeValue);
                                if (matches) addToQueryMatchingAttributes("_", "category");
                                break;
                            default:
                                // Check custom inputs
                                for (const [categoryId, customInputsPair] of Object.entries(product.customInputs || {})) {
                                    for (const [customInputId, customInputData] of Object.entries(customInputsPair)) {
                                        if (customInputId.toLowerCase() === attributeName.toLowerCase() && dataIsMatch(customInputData, attributeValue)) {
                                            matches = true;
                                            addToQueryMatchingAttributes(categoryId, customInputId);
                                        }
                                    }
                                }
                                break;
                        }
                        return matches;
                    });
                });
            } else {
                // Simple text search across all fields
                const queryParts = filterQuery.split(",");
                
                queryParts.forEach(part => {
                    const lowerCasePart = part.toLowerCase().trim();
                    if (!lowerCasePart) return;
                    
                    filteredList = filteredList.filter((product: ProductRead) => {
                        let matches = false;
                        
                        // Check standard fields
                        if (product.sku.toString().toLowerCase().includes(lowerCasePart)) {
                            matches = true;
                            addToQueryMatchingAttributes("_", "sku");
                        }
                        if (product.name.toLowerCase().includes(lowerCasePart)) {
                            matches = true;
                            addToQueryMatchingAttributes("_", "name");
                        }
                        if (product.description && product.description.toLowerCase().includes(lowerCasePart)) {
                            matches = true;
                            addToQueryMatchingAttributes("_", "description");
                        }
                        if (product.location && product.location.toLowerCase().includes(lowerCasePart)) {
                            matches = true;
                            addToQueryMatchingAttributes("_", "location");
                        }
                        if (getLocationNamesString(product.locations || []).toLowerCase().includes(lowerCasePart)) {
                            matches = true;
                            addToQueryMatchingAttributes("_", "location");
                        }
                        if (product.unit && product.unit.toLowerCase().includes(lowerCasePart)) {
                            matches = true;
                            addToQueryMatchingAttributes("_", "unit");
                        }
                        if (product.barcodes?.some((barcode) => barcode.toLowerCase().includes(lowerCasePart))) {
                            matches = true;
                            addToQueryMatchingAttributes("_", "barcode");
                        }
                        
                        // Check custom inputs
                        for (const [categoryId, customInputsPair] of Object.entries(product.customInputs || {})) {
                            for (const [customInputId, customInputData] of Object.entries(customInputsPair)) {
                                // Keywords and booleans don't match in simple search, so we exclude them
                                if (typeof customInputData !== "boolean" && dataIsMatch(customInputData, lowerCasePart)) {
                                    matches = true;
                                    addToQueryMatchingAttributes(categoryId, customInputId);
                                }
                            }
                        }
                        
                        return matches;
                    });
                });
            }
        }

        // Apply category filter
        if (filterCategory) {
            filteredList = filteredList.filter((product: ProductRead) =>
                product.categories && product.categories.includes(filterCategory)
            );
        }

        if (filterLocation) {
            filteredList = filteredList.filter((product: ProductRead) =>
                product.locations && product.locations.includes(filterLocation)
            );
        }

        // Apply stock status filter
        if (filterStockStatus !== StockStatus.ALL) {
            filteredList = filteredList.filter((product: ProductRead) => {
                const amount = product.status?.currentAmount || 0;
                const lowStockWarning = product.stockSettings.lowStockWarning || 0;

                switch (filterStockStatus) {
                    case StockStatus.IN_STOCK:
                        return amount > lowStockWarning;
                    case StockStatus.LOW_STOCK:
                        return amount > 0 && amount <= lowStockWarning;
                    case StockStatus.OUT_OF_STOCK:
                        return amount === 0;
                    case StockStatus.NEGATIVE_STOCK:
                        return amount < 0;
                    default:
                        return true;
                }
            });
        }

        // Apply draft status filter
        if (filterDraftStatus !== ProductDraftFilter.ALL) {
            filteredList = filteredList.filter((product: ProductRead) => {
                const isDraft = product.isDraft === true;
                
                switch (filterDraftStatus) {
                    case ProductDraftFilter.DRAFTS_ONLY:
                        return isDraft;
                    case ProductDraftFilter.PUBLISHED_ONLY:
                        return !isDraft;
                    default:
                        return true;
                }
            });
        }

        // Note: displayDeleted filtering is now handled server-side via the includeDeleted parameter

        // Update query matching attributes
        setQueryMatchingAttributes(newQueryMatchingAttributes);

        return filteredList;
    }, [products, filterQuery, filterCategory, filterLocation, filterStockStatus, filterDraftStatus, getCategoryNamesString, getLocationNamesString, dataIsMatch]);

    // Mutations with cache invalidation
    const createProductMutation = useMutation({
        mutationFn: addProductDoc,
        onSuccess: (_, variables) => {
            trackCreate('product');
            const sid = variables.ids?.storageId;
            const pid = variables.ids?.productId;
            if (!sid) return;
            // Invalidate all product queries for this storage (all sort variations)
            queryClient.invalidateQueries({ queryKey: ['products', sid] });
            if (pid) {
                queryClient.invalidateQueries({ queryKey: ['product', sid, pid] });
            }
            queryClient.invalidateQueries({ queryKey: ['storage', sid] });
            queryClient.invalidateQueries({ queryKey: ['storages'] });
            queryClient.invalidateQueries({ queryKey: ['barcode', sid] });
        },
        onError: (err) => {
            logger.error('Failed to create product', err, 'useProducts');
        }
    });

    const updateProductMutation = useMutation({
        mutationFn: setProductDoc,
        onSuccess: (_, variables) => {
            trackEdit('product');
            const sid = variables.ids?.storageId;
            const pid = variables.ids?.productId;
            if (!sid) return;
            // Invalidate all product queries for this storage (all sort variations)
            queryClient.invalidateQueries({ queryKey: ['products', sid] });
            if (pid) {
                queryClient.invalidateQueries({ queryKey: ['product', sid, pid] });
            }
            queryClient.invalidateQueries({ queryKey: ['barcode', sid] });
        },
        onError: (err) => {
            logger.error('Failed to update product', err, 'useProducts');
        }
    });

    const deleteProductMutation = useMutation({
        mutationFn: deleteProductDoc,
        onSuccess: (_, variables) => {
            trackDelete('product');
            // Invalidate all product queries for this storage (all sort variations)
            queryClient.invalidateQueries({ queryKey: ['products', variables.storageId] });
            queryClient.invalidateQueries({ queryKey: ['product', variables.storageId, variables.productId] });
            queryClient.invalidateQueries({ queryKey: ['barcode', variables.storageId] });
        },
        onError: (err) => {
            logger.error('Failed to delete product', err, 'useProducts');
        }
    });

    const restoreProductMutation = useMutation({
        mutationFn: restoreProductDoc,
        onSuccess: (_, variables) => {
            trackRestore('product');
            // Invalidate all product queries for this storage (all sort variations)
            queryClient.invalidateQueries({ queryKey: ['products', variables.storageId] });
            queryClient.invalidateQueries({ queryKey: ['product', variables.storageId, variables.productId] });
            queryClient.invalidateQueries({ queryKey: ['barcode', variables.storageId] });
        },
        onError: (err) => {
            logger.error('Failed to restore product', err, 'useProducts');
        }
    });

    // Helper function to determine status (keeping the same logic as before)
    const getStatus = (loading: boolean, error: any): FormStatus => {
        if (loading) return FormStatus.LOADING;
        if (error) return FormStatus.ERROR;
        return FormStatus.SUCCESS;
    };

    const reloadProducts = () => {
        if (productId) {
            refetchProduct();
        } else {
            refetchProducts();
        }
    };

    // Check if products are initialized
    const productsInitialized = useMemo(() => {
        if (!storageId) return false;
        if (productId) {
            return getStatus(productLoading, productError) === FormStatus.SUCCESS;
        }
        return getStatus(productsLoading, productsError) === FormStatus.SUCCESS;
    }, [storageId, productId, productLoading, productError, productsLoading, productsError]);

    // Return the exact same interface as before, plus new capabilities
    return {
        // Single product
        product: product || null,
        productStatus: getStatus(productLoading, productError),
        
        // Multiple products
        products,
        filteredProducts,
        productsStatus: getStatus(productsLoading, productsError),
        hasNextPage, // Keeping the same property name
        productsInitialized,
        
        // Filtering and sorting (same as before)
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        filterQuery,
        setFilterQuery,
        filterCategory,
        setFilterCategory,
        filterLocation,
        setFilterLocation,
        filterStockStatus,
        setFilterStockStatus,
        filterDraftStatus,
        setFilterDraftStatus,
        displayDeleted,
        setDisplayDeleted,
        queryMatchingAttributes,

        // Actions (same as before)
        fetchNextPage,
        reloadProducts,
        
        // New React Query capabilities (async only)
        createProductAsync: createProductMutation.mutateAsync,
        updateProductAsync: updateProductMutation.mutateAsync,
        deleteProductAsync: deleteProductMutation.mutateAsync,
        restoreProductAsync: restoreProductMutation.mutateAsync,
        isCreating: createProductMutation.isPending,
        isUpdating: updateProductMutation.isPending,
        isDeleting: deleteProductMutation.isPending,
        isRestoring: restoreProductMutation.isPending,
        isFetchingMore: isFetchingNextPage,
        
        // Refresh functions
        refreshProducts: refetchProducts,
        refreshProduct: refetchProduct,
    };
};

export default useProducts;
